/**
 * Tiny fuzzy matcher for short client-side lists (the sidebar's clinical-tags
 * nav). Deliberately not a library: the lists are tens of entries, so a
 * ranked subsequence match is plenty.
 *
 * Ranking (higher wins): exact > prefix > substring > subsequence. Returns
 * null for no match. Case-insensitive; the query is trimmed.
 */
export function fuzzyScore(query, target) {
    const q = query.trim().toLowerCase();
    const t = target.toLowerCase();
    if (q.length === 0)
        return 0;
    if (t === q)
        return 4;
    if (t.startsWith(q))
        return 3;
    if (t.includes(q))
        return 2;
    // Subsequence: every query char appears in order ("chf" ~ "Congestive Heart Failure"
    // only if c,h,f appear in order — here via "Congestive Heart Failure" c..h..f).
    let i = 0;
    for (const ch of t) {
        if (ch === q[i])
            i++;
        if (i === q.length)
            return 1;
    }
    return null;
}
/**
 * Filter + rank `items` against `query` across each item's searchable strings
 * (label first, aliases after — an alias hit ranks slightly under the same
 * quality of label hit so "CHF" the alias doesn't outrank "CHF" the label).
 * An empty query returns the list unchanged.
 */
export function fuzzyFilter(items, query, getStrings) {
    const q = query.trim();
    if (q.length === 0)
        return items;
    return items
        .map((item, index) => {
        const strings = getStrings(item);
        let best = null;
        for (let s = 0; s < strings.length; s++) {
            const score = fuzzyScore(q, strings[s]);
            if (score === null)
                continue;
            // Label (index 0) wins ties over aliases via a half-point edge.
            const weighted = score - (s > 0 ? 0.5 : 0);
            if (best === null || weighted > best)
                best = weighted;
        }
        return { item, index, best };
    })
        .filter((r) => r.best !== null)
        .sort((a, b) => b.best - a.best || a.index - b.index)
        .map((r) => r.item);
}
